import { useState } from "react"

import { useTranslation } from "react-i18next"

import type { OptionItem } from "../schemas/column-schema"

import { Check, Plus, Trash2 } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

import { useOptionTypesQuery } from "../hooks/use-option-types-query"
import { DEFAULT_OPTION_COLORS } from "../schemas/column-schema"

type OptionsBuilderProps = {
  options: OptionItem[]
  optionTypeId?: string
  onOptionsChange: (options: OptionItem[]) => void
  onOptionTypeIdChange: (optionTypeId: string | undefined) => void
}

export function OptionsBuilder({
  options,
  optionTypeId,
  onOptionsChange,
  onOptionTypeIdChange,
}: OptionsBuilderProps) {
  const { t } = useTranslation()
  const { data: savedOptionTypes = [] } = useOptionTypesQuery()
  const [useExisting, setUseExisting] = useState(!!optionTypeId)

  function getNextColor(currentOptions: OptionItem[]): string {
    const usedColors = new Set(currentOptions.map((opt) => opt.color))
    for (const color of DEFAULT_OPTION_COLORS) {
      if (!usedColors.has(color)) {
        return color
      }
    }
    return DEFAULT_OPTION_COLORS[currentOptions.length % DEFAULT_OPTION_COLORS.length]
  }

  function handleAddOption() {
    const newOption: OptionItem = {
      id: crypto.randomUUID(),
      label: "",
      color: getNextColor(options),
    }
    onOptionsChange([...options, newOption])
  }

  function handleRemoveOption(optionId: string) {
    onOptionsChange(options.filter((opt) => opt.id !== optionId))
  }

  function handleOptionLabelChange(optionId: string, label: string) {
    onOptionsChange(options.map((opt) => (opt.id === optionId ? { ...opt, label } : opt)))
  }

  function handleOptionColorChange(optionId: string, color: string) {
    onOptionsChange(options.map((opt) => (opt.id === optionId ? { ...opt, color } : opt)))
  }

  function handleSelectExistingType(typeId: string) {
    if (typeId === "new") {
      setUseExisting(false)
      onOptionTypeIdChange(undefined)
      if (options.length === 0) {
        handleAddOption()
      }
    } else {
      setUseExisting(true)
      onOptionTypeIdChange(typeId)
      const selectedType = savedOptionTypes.find((optType) => optType.id === typeId)
      if (selectedType) {
        onOptionsChange(selectedType.options)
      }
    }
  }

  return (
    <div className="space-y-4">
      {savedOptionTypes.length > 0 && (
        <div className="space-y-2">
          <Label>{t("pages.ai_parser.options_builder.use_existing")}</Label>
          <Select
            value={useExisting && optionTypeId ? optionTypeId : "new"}
            onValueChange={handleSelectExistingType}
          >
            <SelectTrigger>
              <SelectValue placeholder={t("pages.ai_parser.options_builder.select_or_create")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="new">{t("pages.ai_parser.options_builder.create_new")}</SelectItem>
              {savedOptionTypes.map((optionType) => (
                <SelectItem key={optionType.id} value={optionType.id}>
                  <div className="flex items-center gap-2">
                    <span>{optionType.name}</span>
                    <span className="text-muted-foreground text-xs">
                      ({optionType.options.length}{" "}
                      {t("pages.ai_parser.options_builder.options_count")})
                    </span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="space-y-2">
        <Label>{t("pages.ai_parser.options_builder.options_label")}</Label>
        <div className="space-y-2">
          {options.map((option, index) => (
            <div key={option.id} className="flex items-center gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    aria-label={t("pages.ai_parser.options_builder.color_picker")}
                    className="border-input h-8 w-8 shrink-0 cursor-pointer rounded border"
                    style={{ backgroundColor: option.color }}
                    type="button"
                  />
                </PopoverTrigger>
                <PopoverContent align="start" className="w-auto p-2">
                  <div className="grid grid-cols-6 gap-1">
                    {DEFAULT_OPTION_COLORS.map((color) => (
                      <button
                        key={color}
                        className="relative h-6 w-6 cursor-pointer rounded"
                        style={{ backgroundColor: color }}
                        type="button"
                        onClick={() => handleOptionColorChange(option.id, color)}
                      >
                        {option.color === color && (
                          <Check className="absolute inset-0 m-auto h-4 w-4 text-white drop-shadow-md" />
                        )}
                      </button>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
              <Input
                className="flex-1"
                placeholder={`${t("pages.ai_parser.options_builder.option_placeholder")} ${index + 1}`}
                value={option.label}
                onChange={(event) => handleOptionLabelChange(option.id, event.target.value)}
              />
              <Button
                className="h-8 w-8 p-0"
                disabled={options.length <= 1}
                size="sm"
                type="button"
                variant="ghost"
                onClick={() => handleRemoveOption(option.id)}
              >
                <Trash2 className="h-4 w-4" />
                <span className="sr-only">
                  {t("pages.ai_parser.options_builder.remove_option")}
                </span>
              </Button>
            </div>
          ))}
        </div>
        <Button
          className="w-full"
          size="sm"
          type="button"
          variant="outline"
          onClick={handleAddOption}
        >
          <Plus className="mr-2 h-4 w-4" />
          {t("pages.ai_parser.options_builder.add_option")}
        </Button>
      </div>
    </div>
  )
}
